import React from 'react'
import { ChevronsDownUp, ChevronsUpDown, Copy } from 'lucide-react'
import { SentenceUnit, TranslationViewModel, thaiTextOf } from 'lib/translation-view-model'
import { Translit } from 'lib/transliteration'
import { isThaiText } from 'lib/generic-utils/strings'
import WordWithTooltip from 'components/tooltip/word-with-tooltip'

type Props = {
   model: TranslationViewModel
   activeIndex: number
   expanded: boolean
   onSelectSentence: (index: number) => void
   onToggleExpanded: () => void
   translit: Translit
}

// With this many paragraphs or more the section clamps to a fixed height with
// a fade, so a long paste doesn't push the word breakdown off screen.
const CLAMP_MIN_PARAGRAPHS = 3

const labelClasses = 'font-semibold uppercase tracking-[1.1px] select-none'

const buildCopyText = (model: TranslationViewModel, translit: Translit): string =>
   model.paragraphs
      .map((paragraph) =>
         paragraph.blocks
            .map((block) => {
               const prefix = block.marker ? `${block.marker} ` : ''
               const thai = prefix + block.sentences.map((s) => thaiTextOf(s)).join(' ')
               const romanized = block.sentences
                  .filter((s) => !s.failed)
                  .map((s) => s.words.map((wo) => translit.of(wo.phonetic)).join(' '))
                  .join(' ')
               const english = block.sentences
                  .filter((s) => !s.failed && s.english)
                  .map((s) => s.english)
                  .join(' ')
               // An empty romanization (the visitor sees none) drops out here
               // rather than leaving a blank line in the clipboard.
               return [thai, romanized ? prefix + romanized : '', english ? prefix + english : '']
                  .filter(Boolean)
                  .join('\n')
            })
            .join('\n')
      )
      .join('\n\n')

const FullTranslation = (props: Props) => {
   const { model, activeIndex } = props
   const total = model.sentences.length
   const clampable = model.paragraphs.length >= CLAMP_MIN_PARAGRAPHS
   const clamped = clampable && !props.expanded

   // When the translate worker was unreachable, the fallback lines carry no
   // English at all — skip the row entirely rather than render an empty line
   // under every sentence. Failed lines still need it for their notice.
   const hasEnglishRow = model.sentences.some((unit) => unit.failed || unit.english)

   const [copyMessage, setCopyMessage] = React.useState<string | null>(null)
   const copyMessageTimeout = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
   React.useEffect(() => () => clearTimeout(copyMessageTimeout.current), [])

   const copyTranslation = async () => {
      let message = 'Copied!'
      try {
         await navigator.clipboard.writeText(buildCopyText(model, props.translit))
      } catch {
         message = "Couldn't copy"
      }
      setCopyMessage(message)
      clearTimeout(copyMessageTimeout.current)
      copyMessageTimeout.current = setTimeout(() => setCopyMessage(null), 1500)
   }

   const sentenceSpan = (unit: SentenceUnit, children: React.ReactNode) => (
      <span
         key={unit.index}
         role="button"
         tabIndex={0}
         onClick={() => props.onSelectSentence(unit.index)}
         onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault()
               props.onSelectSentence(unit.index)
            }
         }}
         className={`cursor-pointer rounded-[6px] px-1 py-px mr-0.5 transition-colors ${
            total > 1 && unit.index === activeIndex ? 'bg-lightBlueTransparent' : ''
         }`}
      >
         {children}
      </span>
   )

   return (
      <section className="mt-3">
         <div className="flex items-center justify-between gap-4 mb-3">
            <span className={`text-[12.5px] text-textMuted ${labelClasses}`}>Full translation</span>
            <div className="relative">
               {copyMessage && (
                  <div
                     role="status"
                     className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded-lg border border-solid border-translationChipBorder bg-white px-2.5 py-1 text-[12.5px] font-semibold text-bodyBlack shadow-md pointer-events-none"
                  >
                     {copyMessage}
                  </div>
               )}
               <button
                  type="button"
                  title="Copy translation"
                  aria-label="Copy translation"
                  onClick={copyTranslation}
                  className="w-9 h-9 grid place-items-center rounded-full cursor-pointer border-none text-copyIcon bg-backgroundGray hover:bg-lightBlueTransparent hover:text-accentBlue transition-colors"
               >
                  <Copy size={19} />
               </button>
            </div>
         </div>

         <div className={`relative ${clamped ? 'max-h-[300px] overflow-hidden' : ''}`}>
            <div className="flex flex-col gap-5">
               {model.paragraphs.map((paragraph, pIndex) => (
                  <div key={pIndex}>
                     {model.paragraphs.length > 1 && (
                        <div
                           className={`text-[11.5px] text-paragraphLabel mb-[5px] ${labelClasses}`}
                        >
                           Paragraph {pIndex + 1}
                        </div>
                     )}
                     <div className="flex flex-col gap-2.5">
                        {paragraph.blocks.map((block, bIndex) => (
                           <div key={bIndex} className="flex gap-2.5 items-baseline">
                              {block.marker && (
                                 <span className="flex-none text-[17px] text-accentBlue leading-[1.5]">
                                    {block.marker}
                                 </span>
                              )}
                              <div className="min-w-0">
                                 <div className="leading-[1.55] [text-wrap:pretty] [&_.thai]:text-[25px]!">
                                    {block.sentences.map((unit) =>
                                       sentenceSpan(
                                          unit,
                                          unit.failed ? (
                                             <span
                                                className={
                                                   isThaiText(unit.text)
                                                      ? 'thai text-[25px]!'
                                                      : undefined
                                                }
                                             >
                                                {unit.text}
                                             </span>
                                          ) : (
                                             unit.words.map((wo, i) => (
                                                <WordWithTooltip wordObject={wo} key={i} />
                                             ))
                                          )
                                       )
                                    )}
                                 </div>
                                 {props.translit.enabled && (
                                    <div className="text-[16.5px] italic leading-[1.5] text-translit mt-[3px] [text-wrap:pretty]">
                                       {block.sentences.map((unit) =>
                                          unit.failed
                                             ? null
                                             : sentenceSpan(
                                                  unit,
                                                  unit.words
                                                     .map(
                                                        (wo) =>
                                                           `${props.translit.of(wo.phonetic)}${wo.hasTrailingSpace ? ' ·' : ''}`
                                                     )
                                                     .join(' ')
                                               )
                                       )}
                                    </div>
                                 )}
                                 {hasEnglishRow && (
                                    <div className="text-[16.5px] leading-[1.55] text-bodyBlack mt-1 [text-wrap:pretty]">
                                       {block.sentences.map((unit) =>
                                          sentenceSpan(
                                             unit,
                                             unit.failed ? (
                                                <span className="italic opacity-60">
                                                   Couldn&rsquo;t translate this line
                                                </span>
                                             ) : (
                                                unit.english
                                             )
                                          )
                                       )}
                                    </div>
                                 )}
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               ))}
            </div>
            {clamped && (
               <div className="absolute inset-x-0 bottom-0 h-[90px] pointer-events-none bg-linear-to-b from-transparent to-white to-[78%]" />
            )}
         </div>

         {clampable && (
            <button
               type="button"
               onClick={props.onToggleExpanded}
               className="inline-flex items-center gap-1.5 mt-2.5 text-[13.5px] font-semibold text-accentBlue cursor-pointer bg-transparent border border-solid border-translationChipBorder rounded-lg px-3 py-1.5 hover:bg-lightBlueTransparent transition-colors"
            >
               {clamped ? <ChevronsUpDown size={17} /> : <ChevronsDownUp size={17} />}
               {clamped ? `Show all ${total} ${model.unitNoun}s` : 'Collapse'}
            </button>
         )}
      </section>
   )
}

export default FullTranslation
